import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import { describeProvisioningError, sendWelcomeEmail } from '../../../common/welcomeEmailApi.js';
import { nameFromEmail } from '../../../common/welcomeEmailTemplate.js';
import WelcomeEmailChoice from './WelcomeEmailChoice.jsx';
import WelcomeEmailPreview from './WelcomeEmailPreview.jsx';

// Sends (or resends) one user a Welcome Email. A new send retires the user's earlier links.
const SendWelcomeEmailDialog = ({ user, onClose, templates, showMessage }) => {
    const [templateId, setTemplateId] = useState('');
    const [target, setTarget] = useState('insights');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (user) {
            setTarget('insights');
            setError('');
        }
    }, [user]);

    useEffect(() => {
        if (!user || templates.length === 0) return;
        setTemplateId((current) =>
            templates.some((template) => template.id === current) ? current : templates[0].id,
        );
    }, [user, templates]);

    const template = templates.find((candidate) => candidate.id === templateId) || null;
    // Older users have no email on their user doc; the function sends to the login's email anyway.
    const email = user?.email || '';
    const recipientLabel = email || 'brugerens login-email';

    const send = async () => {
        setSending(true);
        setError('');
        try {
            const result = await sendWelcomeEmail({ uid: user.id, templateId, target });
            if (result.email_outcome === 'sent') {
                showMessage(`Velkomstmail sendt til ${recipientLabel}.`);
                onClose();
            } else {
                setError('Mailen kunne ikke sendes. Prøv igen om lidt.');
            }
        } catch (sendError) {
            console.error('Error sending welcome email:', sendError);
            setError(describeProvisioningError(sendError));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog
            open={Boolean(user)}
            onClose={sending ? undefined : onClose}
            className="setup-dialog"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Send velkomstmail</DialogTitle>
            <DialogContent className="setup-user-dialog-content">
                <p className="welcome-section-note">
                    Et nyt link sendes til {recipientLabel}. Tidligere links fra velkomstmails holder op med at virke.
                </p>
                <WelcomeEmailChoice
                    templates={templates}
                    templateId={template ? templateId : ''}
                    onTemplateChange={setTemplateId}
                    target={target}
                    onTargetChange={setTarget}
                    disabled={sending}
                />
                {template && (
                    <WelcomeEmailPreview
                        template={template}
                        email={email || recipientLabel}
                        name={user?.label || nameFromEmail(email)}
                    />
                )}
                {sending && <LinearProgress />}
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>
                    Annuller
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={send}
                    disabled={sending || !template}
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SendWelcomeEmailDialog;
